/**
 * Address Parser Utilities
 *
 * Functions for parsing Google Places API results into
 * our address data structure.
 */
import type { AddressComponents } from "@/types/address";

import { normalizeStateName } from "./normalizeAddress";

/**
 * Google address component type mappings to our field names
 */
const COMPONENT_TYPE_MAP: Record<string, keyof AddressComponents> = {
  street_number: "exterior_number",
  route: "street",
  sublocality_level_1: "neighborhood",
  sublocality: "neighborhood",
  neighborhood: "neighborhood",
  locality: "city",
  administrative_area_level_1: "state",
  administrative_area_level_2: "municipality",
  postal_code: "zip_code",
  country: "country",
};

/**
 * Google Places address component interface
 */
interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

/**
 * Parse Google Places address components into our format
 *
 * @param components - Address components from Google Places API
 * @param formattedAddress - The formatted address string
 * @returns Parsed AddressComponents object
 *
 * @example
 * const result = parseGoogleAddressComponents(
 *   place.address_components,
 *   place.formatted_address
 * );
 */
export const parseGoogleAddressComponents = (
  components: GoogleAddressComponent[] | undefined,
  formattedAddress: string = "",
): AddressComponents => {
  const result: AddressComponents = {
    street: "",
    exterior_number: "",
    interior_number: "", // Google Places doesn't provide this
    neighborhood: "",
    city: "",
    municipality: "",
    state: "",
    zip_code: "",
    country: "",
    formatted_address: formattedAddress,
  };

  if (!components) return result;

  for (const component of components) {
    for (const type of component.types) {
      const mappedField = COMPONENT_TYPE_MAP[type];
      if (mappedField && !result[mappedField]) {
        // Use long_name for most fields
        let value = component.long_name;

        // Normalize state name
        if (mappedField === "state") {
          value = normalizeStateName(value);
        }

        result[mappedField] = value;
      }
    }
  }

  return result;
};

/**
 * Extract street number from a street address string
 * Handles Mexican address formats like "Av. Insurgentes Sur 1234"
 *
 * @param street - Street address string
 * @returns Object with street name and number
 */
export const extractStreetNumber = (
  street: string,
): { street: string; number: string } => {
  if (!street) return { street: "", number: "" };

  // Common Mexican patterns:
  // "Calle 123" -> street: "Calle", number: "123"
  // "Av. Insurgentes Sur 1234" -> street: "Av. Insurgentes Sur", number: "1234"
  // "Paseo de la Reforma 505" -> street: "Paseo de la Reforma", number: "505"

  // Try to match a number at the end
  const match = street.match(/^(.+?)\s+(\d+[A-Za-z]?)$/);

  if (match) {
    return {
      street: match[1].trim(),
      number: match[2],
    };
  }

  // Try to match a number at the beginning (less common)
  const matchStart = street.match(/^(\d+[A-Za-z]?)\s+(.+)$/);

  if (matchStart) {
    return {
      street: matchStart[2].trim(),
      number: matchStart[1],
    };
  }

  // No number found
  return { street: street.trim(), number: "" };
};

/**
 * Validate that an AddressComponents object has required fields
 *
 * @param address - AddressComponents to validate
 * @returns Object with isValid flag and array of missing fields
 */
export const validateAddressComponents = (
  address: AddressComponents,
): { isValid: boolean; missingFields: string[] } => {
  const requiredFields: (keyof AddressComponents)[] = [
    "street",
    "exterior_number",
    "neighborhood",
    "city",
    "state",
    "zip_code",
  ];

  const missingFields = requiredFields.filter((field) => !address[field]);

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

/**
 * Format an AddressComponents object as a single line string
 *
 * @param address - AddressComponents to format
 * @returns Formatted address string
 */
export const formatAddressOneLine = (address: AddressComponents): string => {
  const parts: string[] = [];

  if (address.street) {
    let streetPart = address.street;
    if (address.exterior_number) {
      streetPart += ` ${address.exterior_number}`;
    }
    if (address.interior_number) {
      streetPart += ` Int. ${address.interior_number}`;
    }
    parts.push(streetPart);
  }

  if (address.neighborhood) {
    parts.push(address.neighborhood);
  }

  if (address.city) {
    parts.push(address.city);
  }

  if (address.state) {
    parts.push(address.state);
  }

  if (address.zip_code) {
    parts.push(`C.P. ${address.zip_code}`);
  }

  return parts.join(", ");
};
